import React, { useState, useEffect, forwardRef } from 'react';
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface EditableFieldProps {
  label: string;
  value?: string;
  placeholder: string;
  tooltipText: string;
  onChange?: (value: string) => void;
  lines?: number;
}

const EditableField = forwardRef<HTMLDivElement, EditableFieldProps>(({
  label,
  value,
  placeholder,
  tooltipText,
  onChange,
  lines = 2
}, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || '');
  
  const handleSave = () => {
    if (onChange) onChange(tempValue);
    setIsEditing(false);
  };

  const handleClear = () => {
    setTempValue('');
  };
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only process if we're in edit mode
      if (!isEditing) return;
      
      // Command+S (Mac) or Ctrl+S (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault(); // Prevent browser's save dialog
        handleSave();
      }
      
      // Escape key to cancel
      if (e.key === 'Escape') {
        e.preventDefault();
        setTempValue(value || '');
        setIsEditing(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, tempValue, value]);
  
  // Update tempValue if external value changes
  useEffect(() => {
    setTempValue(value || '');
  }, [value]);

  return (
    <div className="space-y-1" ref={ref}>
      <label className="text-[11px] font-medium block text-muted-foreground">
        {label}
      </label>
      
      {isEditing ? (
        <div className="space-y-1">
          <div className="relative">
            <Textarea 
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              placeholder={placeholder}
              rows={lines}
              className="text-xs p-2 min-h-[2.5rem] whitespace-pre-wrap overflow-wrap-break-word"
              autoFocus
            />
            
            {tempValue && (
              <button
                type="button"
                className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
                onClick={handleClear}
                aria-label="Clear text"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          
          <div className="flex justify-end space-x-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => {
                setTempValue(value || '');
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-6 text-xs"
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p 
                className={cn(
                  "text-xs min-h-[2.5rem] cursor-pointer hover:bg-muted/40 p-1 rounded whitespace-pre-wrap overflow-wrap-break-word",
                  value ? "text-foreground font-normal" : "text-muted-foreground italic",
                  lines === 3 && "line-clamp-3",
                  lines === 2 && "line-clamp-2",
                  lines === 1 && "line-clamp-1"
                )}
                onClick={() => {
                  setTempValue(value || '');
                  setIsEditing(true);
                }}
              >
                {value || placeholder}
              </p>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[250px]">
              <p>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
});

EditableField.displayName = 'EditableField';

export default EditableField;
